import { useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import Modal from 'src/modules/shared/components/molecules/modals/modal';
import ModalFooter from 'src/modules/shared/components/molecules/modals/modal-footer';
import LabelTextInput from 'src/modules/shared/components/atoms/inputs/label-text-input';
import LabelTextArea from 'src/modules/shared/components/atoms/inputs/label-text-area';
import LabelSelect from 'src/modules/shared/components/atoms/inputs/label-select';
import { useFunnelAddresses } from 'src/modules/communications/hooks/api/funnels/funnel-addresses';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import type { CrmCreateTestConversationResponse } from 'src/api/generated/models/CrmCreateTestConversationResponse';
import { useCreateTestConversation } from 'src/modules/shared/hooks/api/crm/test-conversations.mutation';
import { ChannelType, type FunnelAddress } from 'src/api/generated';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

interface CreateTestConversationModalProps {
  isOpen: boolean;
  onClose: () => void;
  funnelId: number;
  onCreated: (conversationId: number) => void;
}

export const CreateTestConversationModal: React.FC<
  CreateTestConversationModalProps
> = ({ isOpen, onClose, funnelId, onCreated }) => {
  const { t } = useI18n();
  const createTestConversationSchema = z.object({
    fromPhoneNumber: z
      .string()
      .min(1, t('communications.testConversations.form.fromPhoneRequired')),
    contactDisplayName: z.string().optional(),
    initialMessage: z
      .string()
      .min(
        1,
        t('communications.testConversations.form.initialMessageRequired')
      ),
  });
  type CreateTestConversationFormData = z.infer<
    typeof createTestConversationSchema
  >;

  const addressesQuery = createQuery()
    .equals('funnelId', funnelId)
    .orderByDesc('createdAt')
    .build();
  const { addresses, loading: loadingAddresses } = useFunnelAddresses(
    addressesQuery,
    isOpen && !!funnelId
  );

  const whatsappAddresses = addresses.filter(
    (address: FunnelAddress) => address.channelType === ChannelType.WHATSAPP
  );
  const defaultFromPhoneNumber = whatsappAddresses[0]?.address ?? '';

  const { createTestConversation, loading: creatingConversation } =
    useCreateTestConversation((data: CrmCreateTestConversationResponse) => {
      onCreated(data.conversation.id);
      onClose();
    });
  const idempotencyKeyRef = useRef<string>('');

  const {
    register,
    handleSubmit,
    control,
    getValues,
    reset,
    setValue,
    formState: { errors },
  } = useForm<CreateTestConversationFormData>({
    resolver: zodResolver(createTestConversationSchema),
    defaultValues: {
      fromPhoneNumber: '',
      contactDisplayName: '',
      initialMessage: '',
    },
  });

  useEffect(() => {
    if (!isOpen) {
      idempotencyKeyRef.current = '';
      reset({
        fromPhoneNumber: '',
        contactDisplayName: '',
        initialMessage: '',
      });
      return;
    }

    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = globalThis.crypto.randomUUID();
    }

    if (getValues('fromPhoneNumber') || !defaultFromPhoneNumber) {
      return;
    }

    setValue('fromPhoneNumber', defaultFromPhoneNumber, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: true,
    });
  }, [defaultFromPhoneNumber, getValues, isOpen, reset, setValue]);

  const handleCreate = async (data: CreateTestConversationFormData) => {
    await createTestConversation({
      fromPhoneNumber: data.fromPhoneNumber,
      idempotencyKey:
        idempotencyKeyRef.current || globalThis.crypto.randomUUID(),
      contactDisplayName: data.contactDisplayName ?? undefined,
      initialMessage: data.initialMessage,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header={
        <h2 className="text-lg font-semibold">
          {t('communications.testConversations.actions.create')}
        </h2>
      }
      dialogClassName="sm:max-w-xl"
      bodyClassName="px-4 py-4 sm:px-5"
      body={
        <form className="flex w-full flex-col gap-4">
          {whatsappAddresses.length > 1 && (
            <Controller
              name="fromPhoneNumber"
              control={control}
              render={({ field }) => (
                <LabelSelect
                  label={t('communications.testConversations.form.lineLabel')}
                  labelPosition="top"
                  options={whatsappAddresses.map((address) => ({
                    value: address.address,
                    content: address.address,
                  }))}
                  selectProps={{
                    value: field.value,
                    onChange: (event) => field.onChange(event.target.value),
                    disabled:
                      loadingAddresses || whatsappAddresses.length === 0,
                  }}
                />
              )}
            />
          )}

          <LabelTextInput
            label={t('communications.testConversations.form.contactNameLabel')}
            labelPosition="top"
            inputProps={{
              placeholder: t(
                'communications.testConversations.form.contactNamePlaceholder'
              ),
              error: errors.contactDisplayName?.message,
              ...register('contactDisplayName'),
            }}
          />

          <LabelTextArea
            label={t(
              'communications.testConversations.form.initialMessageLabel'
            )}
            labelPosition="top"
            inputProps={{
              rows: 4,
              placeholder: t(
                'communications.testConversations.form.initialMessagePlaceholder'
              ),
              error: errors.initialMessage?.message,
              ...register('initialMessage'),
            }}
          />
        </form>
      }
      footer={
        <ModalFooter
          onCancel={onClose}
          onConfirm={handleSubmit(handleCreate)}
          cancelText={t('shared.actions.cancel')}
          confirmText={t(
            'communications.testConversations.actions.confirmCreate'
          )}
          loading={creatingConversation}
          confirmDisabled={whatsappAddresses.length === 0}
        />
      }
    />
  );
};
